import { useEffect, useRef } from "react"

import { useAIExplorerChat } from "@/features/ai-explorer/hooks/use-ai-explorer-chat"
import { useDashStore } from "@/stores/dash-store"

import { getDashRequestIntent } from "../utils/dash-request-intent"
import { generateDashPrompt } from "../utils/prompt-generator"

// Build the per-component description+example block once per session.
// `generateDashPrompt` is a pure function over a static schema list, so
// computing it at module load avoids re-running on every chat call.
//
// §1.7: the frontend ships this catalog as the `dash_component_catalog`
// override on every Dash chat request. The backend's `build_explorer_
// system_prompt` appends it to the system message under "Dash Component
// Catalog (live)", so the agent always sees the live renderable surface
// rather than whatever hand-maintained subset lives in `dash.md`.
const DASH_COMPONENT_CATALOG = generateDashPrompt()

/**
 * Decode the `?dash_state=<base64-json>` URL parameter and pull
 * `connection_id` out. Reading directly from the URL keeps this hook
 * decoupled from `DashboardStateProvider` — that provider scopes to a
 * dashboard renderer subtree which doesn't always wrap the chat hook.
 *
 * Returns null when the URL has no dash_state, the payload is malformed,
 * or it carries no `connection_id` string. The agent sees no
 * `connection_id` override in those cases, matching pre-existing behaviour
 * for landing-page chats without a selected dashboard.
 */
function readConnectionIdFromUrl(): string | null {
  if (typeof window === "undefined") return null
  try {
    const encoded = new URLSearchParams(window.location.search).get("dash_state")
    if (!encoded) return null
    const padded =
      encoded.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (encoded.length % 4)) % 4)
    const bin = atob(padded)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes))
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null
    const cid = (parsed as { connection_id?: unknown }).connection_id
    return typeof cid === "string" && cid.trim() ? cid.trim() : null
  } catch {
    return null
  }
}

export function useDashChat() {
  // Dashboard generation needs a reasoning-capable model: the dash
  // agent runs a 7-step tool flow (discover → describe → sample →
  // SQL → validate → dry_run → bind) with 36 tools and a 880-line
  // system prompt. Smaller models skip the tool flow entirely and
  // hallucinate dashboard YAML + invented connection_ids (observed
  // prod failure 2026-05-12 with gpt-5-nano: 0 tool calls, fake
  // doris-tickets-conn-01).
  //
  // Pin two things explicitly:
  //   - `modelOverride: "auto"` — ride the system auto model, which the
  //     backend resolves to the org's configured reasoning model
  //     (currently gpt-5.4). This pins AWAY from a user's weak personal
  //     default (e.g. gpt-5-nano) — without which the dash agent emits
  //     SQL against forbidden columns like `ticket_fact.quantity`
  //     (always NULL) and `date_dimension.season` (doesn't exist),
  //     producing tiles with `value: 0`. Using "auto" rather than a
  //     hard-coded model means dash follows the org when the auto model
  //     is bumped (was previously pinned to gpt-5_eu, which stuck dash on
  //     the old gpt-5 even after 5.4 shipped). Reasoning modes no longer
  //     force a model, so "auto" is the right way to demand a strong one.
  //   - `reasoningMode: "thinking"` — used only as a non-auto mode signal so
  //     the send path does not fall into Auto's effort heuristic. Slash
  //     presets now map Thinking → high by default, which is too slow for
  //     Dash (high/`big_brain` spent 30+ min and SSE keep-alive dropped —
  //     Task #16). Pin `effortOverride: "medium"` so Dash stays on the
  //     5–7 min path even when the Thinking preset (or its re-resolve
  //     effect) would otherwise lock high.
  // useAIExplorerChat reads request settings from DashStore, so defaults and
  // the model picker must update that same store. The old implementation wrote
  // to AIExplorerStore while requests read from DashStore.
  const explorerStore = useDashStore("dash")
  const reasoningMode = explorerStore((state) => state.reasoningMode)
  const setReasoningMode = explorerStore((state) => state.setReasoningMode)
  const modelOverride = explorerStore((state) => state.modelOverride)
  const setModelOverride = explorerStore((state) => state.setModelOverride)
  const effortOverride = explorerStore((state) => state.effortOverride)
  const setEffortOverride = explorerStore((state) => state.setEffortOverride)
  const defaultsInitializedRef = useRef(false)

  useEffect(() => {
    if (defaultsInitializedRef.current) return
    defaultsInitializedRef.current = true
    if (reasoningMode === "auto") {
      setReasoningMode("thinking")
    }
    if (modelOverride === null) {
      setModelOverride("auto")
    }
    if (effortOverride === null) {
      setEffortOverride("medium")
    }
  }, [
    reasoningMode,
    setReasoningMode,
    modelOverride,
    setModelOverride,
    effortOverride,
    setEffortOverride,
  ])

  return useAIExplorerChat({
    useStore: useDashStore,
    agentName: "dash",
    getExtraOverrides: (store) => {
      const idx = store.getState().selectedTileIndex
      // The dash agent needs `connection_id` to know which warehouse to
      // discover schema for. The frontend persists it in the
      // `?dash_state=<base64>` URL parameter (read on every send so URL
      // edits / browser-back propagate), but no path previously forwarded
      // it to chat overrides. Without it Big Brain refuses to hallucinate
      // a fake connection (correctly) and returns a placeholder dashboard
      // — observed 2026-05-12. Forward verbatim so the prompt builder can
      // surface it in the system message.
      const connectionId = readConnectionIdFromUrl()
      const currentDashboardSpec = store.getState().currentDashboardSpec
      return {
        ...(idx !== null && idx >= 0 ? { selected_tile_index: idx } : {}),
        ...(connectionId ? { connection_id: connectionId } : {}),
        dash_request_intent: getDashRequestIntent({
          currentDashboardId: store.getState().currentDashboardId,
          currentDashboardSpec,
        }),
        ...(currentDashboardSpec
          ? { dash_current_spec: currentDashboardSpec as Record<string, unknown> }
          : {}),
        dash_component_catalog: DASH_COMPONENT_CATALOG,
      }
    },
  })
}
